import React, { useState } from "react";
import { Chip, Autocomplete, TextField } from "@mui/material";
import {
  getFilteredReactionOptions,
  REACTION_OPTIONS,
} from "../utils/utils.js";
import CheckIcon from "@mui/icons-material/Check";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const PACING_CATEGORY = "Pacing"; // mutually exlusive category, only one pacing option allowed to be chosen

const ChipInput = React.forwardRef(function ChipInput(
  { selectedTags, onTagsChange, contentType, readOnly },
  inputRef,
) {
  const [inputValue, setInputValue] = useState(""); // controls value of textfield input
  const [open, setOpen] = useState(false);

  const filteredReactionOptions = React.useMemo(
    () => getFilteredReactionOptions(contentType),
    [contentType],
  );

  return (
    <Autocomplete
      multiple
      id="reaction-input"
      options={filteredReactionOptions}
      getOptionLabel={(option) => option.label}
      open={readOnly ? false : open}
      onOpen={() => {
        if (!readOnly) setOpen(true);
      }}
      onClose={() => setOpen(false)}
      // groupBy={(option) => option.category} // removed for the time being as different padding
      onFocus={(event) => {
        if (readOnly) {
          event.preventDefault();
          event.stopPropagation();
          event.target.blur();
        }
      }}
      openOnFocus
      value={selectedTags}
      inputValue={inputValue}
      // forcePopupIcon={false}
      popupIcon={
        readOnly ? null : open ? (
          <CheckIcon sx={{ transform: "rotate(180deg)", fontSize: 20 }} />
        ) : (
          <ArrowDropDownIcon />
        )
      }
      disableCloseOnSelect
      // disableClearable
      fullWidth
      onChange={(event, selectedOptions) => {
        if (readOnly) return;
        // fires when an input is selected from dropdown (or typed and entered in freesolo)
        const selectedValues = selectedOptions.map((option) =>
          typeof option === "object" ? option.label : option,
        ); // extract the reaction values from the reaction object
        const lastSelected = selectedOptions[selectedOptions.length - 1];

        if (lastSelected?.category === PACING_CATEGORY) {
          // overwrite existing pacing option if a second one is applied
          const withoutOldPacing = selectedTags.filter((label) => {
            const opt = REACTION_OPTIONS.find((o) => o.label === label);
            return opt?.category !== PACING_CATEGORY;
          });

          onTagsChange([...withoutOldPacing, lastSelected.label]);
        } else {
          onTagsChange(selectedValues);
        }
        setInputValue(""); // clears textfield input
      }}
      renderTags={(value, getTagProps) =>
        value.map((value, index) => {
          // Find the corresponding object from REACTION_OPTIONS based on the label
          const option = REACTION_OPTIONS.find(
            (option) => option.label === value,
          );
          const tagProps = getTagProps({ index });
          return (
            <Chip
              variant="filled"
              icon={
                option.icon || (
                  <span role="img" aria-label={option.label}>
                    {option.emoji}
                  </span>
                )
              }
              label={value}
              size="small"
              {...tagProps}
              onDelete={readOnly ? undefined : tagProps.onDelete}
            />
          );
        })
      }
      renderInput={(
        params, // renders input field where user can type
      ) => (
        <>
          <TextField
            {...params}
            inputRef={inputRef}
            autoFocus
            placeholder={selectedTags.length === 0 ? "Add reactions" : ""}
            size="small"
            fullWidth
            tabIndex={readOnly ? -1 : 0}
            InputProps={{
              ...params.InputProps,
              // disableUnderline: true,
              // readOnly: readOnly,
              // sx: {
              //   ".MuiInputBase-input": {
              //     fontSize: "0.875rem",
              //     position: "relative",
              //   },
              // },
            }}
            onChange={(event) => {
              if (readOnly) return;
              const value = event.target.value || "";
              setInputValue(value);
            }} // inputValue state updated as the user types
          />
        </>
      )}
      isOptionEqualToValue={(option, value) => option.label === value}
      renderOption={(props, option) => (
        <li {...props} style={{ padding: "4px", minHeight: "auto" }}>
          <Chip
            variant="filled"
            icon={
              option.icon || (
                <span role="img" aria-label={option.label}>
                  {option.emoji}
                </span>
              )
            }
            label={option.label}
            size="small"
          />
        </li>
      )}
      ListboxProps={{ sx: { maxHeight: "20vh" } }}
      sx={{
        "& .MuiAutocomplete-endAdornment": {
          // make end adornment always align to top of input
          top: 6,
          transform: "none",
        },
      }}
    />
  );
});

export default ChipInput;
